<script setup lang="ts">
import axios from 'axios'
import { ExternalLink, MoreHorizontal } from 'lucide-vue-next'

import useNotification from '@/composables/notification'
import useOffer from '@/composables/offer'
import { getApiErrorMessage } from '@/utils/apiError.js'

import type { ColumnDef, PaginationState, RowData } from '@/components/ui/Table/interfaces'
import type { OfferPromotionalCode } from '@/types/types'

interface PromotionalCodeRowData {
  value: string
  usage: {
    uses: number
    usageLimit: number
  }
  createdAt: string
}

const props = withDefaults(
  defineProps<{
    offerId: number
    embedded?: boolean
  }>(),
  {
    embedded: false
  }
)

const { t } = useI18n()
const notification = useNotification()
const { fetchPromotionalCodes } = useOffer()
const { user } = useLocalStorage()
const { openInNewTab } = useDOM()

const promotionalCodes = ref<OfferPromotionalCode[]>([])
const isLoading = ref(false)
const webappBaseUrl = ref<string | null>(null)

const pagination = ref<PaginationState>({
  currentPage: 1,
  lastPage: 1,
  total: 0,
  pageSize: 50
})

const columns = computed<Record<string, ColumnDef>>(() => ({
  value: {
    label: t('pages.offer.detail.promotionalCodeList.table.code')
  },
  usage: {
    label: t('pages.offer.detail.promotionalCodeList.table.usage')
  },
  actions: {
    label: '',
    headerClass: 'w-10! pl-0!'
  }
}))

const rows = computed<RowData[]>(() =>
  promotionalCodes.value.map((code) => ({
    id: code.id,
    data: {
      value: code.value,
      usage: {
        uses: code.uses,
        usageLimit: code.usageLimit
      }
    }
  }))
)

const resolveWebappBaseUrl = async (): Promise<string | null> => {
  if (webappBaseUrl.value) {
    return webappBaseUrl.value
  }

  const storedLink = user.value.merchant.webappLink?.replace(/\/$/, '')
  if (storedLink) {
    webappBaseUrl.value = storedLink
    return storedLink
  }

  try {
    const { data } = await axios.get<{ data: { webappLink?: string } }>(`/merchants/${user.value.merchant.id}`)
    const link = data.data.webappLink?.replace(/\/$/, '')
    if (!link) {
      return null
    }

    webappBaseUrl.value = link
    user.value.merchant.webappLink = link
    return link
  } catch {
    return null
  }
}

const openPromoCodePage = async (code: string) => {
  const base = await resolveWebappBaseUrl()
  if (!base) {
    notification.create('error', {
      text: t('pages.offer.detail.promotionalCodeList.actions.openPageError')
    })
    return
  }

  openInNewTab(`${base}/promo-code/${encodeURIComponent(code)}`)
}

const loadPromotionalCodes = async (page = pagination.value.currentPage) => {
  isLoading.value = true

  try {
    const response = await fetchPromotionalCodes(props.offerId, page, pagination.value.pageSize ?? 50)

    promotionalCodes.value = response.data ?? []
    pagination.value = {
      currentPage: response.meta.current_page,
      lastPage: response.meta.last_page,
      total: response.meta.total,
      pageSize: pagination.value.pageSize
    }
  } catch (error: unknown) {
    promotionalCodes.value = []
    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('pages.offer.detail.promotionalCodeList.fetchError')
    })
  } finally {
    isLoading.value = false
  }
}

const handleChangePage = (page: number) => {
  pagination.value.currentPage = page
  loadPromotionalCodes(page)
}

const refresh = () => loadPromotionalCodes(1)

defineExpose({ refresh })

const getRowData = (data: unknown): PromotionalCodeRowData => data as PromotionalCodeRowData

onMounted(() => {
  loadPromotionalCodes()
})
</script>

<template>
  <AppTable
    :columns="columns"
    :rows="rows"
    :pagination="pagination"
    :is-loading="isLoading"
    :embedded="embedded"
    @update:change-page="handleChangePage"
  >
    <template #td-value="{ data }">
      <BaseChip
        :color="getRowData(data).usage.uses >= getRowData(data).usage.usageLimit ? 'gray' : 'violet'"
        :text="data.value as string"
      />
    </template>

    <template #td-usage="{ data }">
      <p class="text-right">
        {{
          t('pages.offer.detail.promotionalCodeList.usageValue', {
            uses: getRowData(data).usage.uses,
            limit: getRowData(data).usage.usageLimit
          })
        }}
      </p>
    </template>

    <template #th-usage>
      <p class="text-right">
        {{ t('pages.offer.detail.promotionalCodeList.table.usage') }}
      </p>
    </template>

    <template #td-actions="{ data }">
      <div class="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="outline" size="icon-sm">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem @click="openPromoCodePage(getRowData(data).value)">
              <ExternalLink />
              {{ t('pages.offer.detail.promotionalCodeList.actions.openPage') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </template>

    <template #empty-state>
      <p class="text-sm text-muted-foreground">
        {{ t('pages.offer.detail.promotionalCodeList.empty') }}
      </p>
    </template>
  </AppTable>
</template>
